import { useState } from 'react';
import { LinkIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

/** The retailer's own favicon, so a list of links reads as a list of stores.
 * Google's service is used rather than `<host>/favicon.ico`, which most large
 * retailers 404. A URL the browser can't parse just gets no mark. */
function hostOf(url: string): string {
  try {
    // Stripped to match the host the backend resolves, or a resolved
    // "ebay.com" and an unresolved "www.ebay.com" key as two stores.
    return new URL(url).hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    return '';
  }
}

/** Retailer host resolved server-side, so shorteners key on the real store. */
export interface ResolvedLinkHost {
  link: string;
  host: string;
}

/**
 * A shortened link's own host is the shortener, not the store: without the
 * resolved host every amzn.to link keys the same and renders the same globe.
 */
export function storeHostOf(
  url: string,
  resolved?: ResolvedLinkHost[]
): string {
  return resolved?.find((entry) => entry.link === url)?.host || hostOf(url);
}

/** One mark per store, so two links to the same retailer show one favicon. */
export function dedupeByHost(
  urls: string[],
  resolved?: ResolvedLinkHost[]
): string[] {
  const seen = new Set<string>();
  return urls.filter((url) => {
    const key = storeHostOf(url, resolved) || url;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export default function StoreIcon({
  url,
  host: resolvedHost,
  className = 'h-6 w-6',
}: {
  url: string;
  host?: string;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  const host = resolvedHost || hostOf(url);
  if (!host || failed) {
    return <LinkIcon className={cn('shrink-0', className)} />;
  }
  return (
    <img
      src={`https://www.google.com/s2/favicons?domain=${host}&sz=64`}
      alt=""
      onError={() => setFailed(true)}
      className={cn('shrink-0 rounded', className)}
    />
  );
}
